import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "@mui/material";
import { IoClose, IoChevronDown, IoCheckmark } from "react-icons/io5";

const PRESETS = [
    { value: 0.5, label: "0.5%", note: "Best price · stable pairs" },
    { value: 1, label: "1%", note: "Balanced · most trades" },
    { value: 2, label: "2%", note: "Fills through volatility" },
];

const STORAGE_KEY = "usdx_slippage";

/** Status of a tolerance value, used for both the banner and the trigger dot. */
function assess(value) {
    if (!value || value <= 0) return null;
    if (value < 0.5) {
        return {
            tone: "warning",
            text: "Tight tolerance. The trade will revert if the price moves even slightly before it confirms.",
        };
    }
    if (value > 5) {
        return {
            tone: "danger",
            text: "Wide tolerance. You could receive noticeably less than quoted, and the trade becomes worth front-running.",
        };
    }
    return {
        tone: "success",
        text: "Sensible range. Enough room to confirm without giving away much on price.",
    };
}

const TONE_STYLES = {
    warning: {
        wrap: "bg-usdx-warning/10 border-usdx-warning/25",
        text: "text-usdx-warning",
        dot: "bg-usdx-warning",
    },
    danger: {
        wrap: "bg-usdx-danger/10 border-usdx-danger/25",
        text: "text-usdx-danger-strong",
        dot: "bg-usdx-danger",
    },
    success: {
        wrap: "bg-usdx-success/10 border-usdx-success/25",
        text: "text-usdx-success-strong",
        dot: "bg-usdx-success",
    },
};

const SlippageSettings = ({ open, setOpen, slippage, setSlippage }) => {
    const [customValue, setCustomValue] = useState("");
    const [isCustom, setIsCustom] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const customInputRef = useRef(null);

    // Reconcile incoming slippage with the preset list
    useEffect(() => {
        const match = PRESETS.some((p) => p.value === slippage);
        setIsCustom(!match);
        setCustomValue(match ? "" : String(slippage));
    }, [slippage]);

    // Reset the menu whenever the dialog is dismissed
    useEffect(() => {
        if (!open) setMenuOpen(false);
    }, [open]);

    // Dismiss the menu on an outside click or Escape
    useEffect(() => {
        if (!menuOpen) return;

        const onPointerDown = (e) => {
            if (
                !menuRef.current?.contains(e.target) &&
                !triggerRef.current?.contains(e.target)
            ) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                setMenuOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener("pointerdown", onPointerDown, true);
        document.addEventListener("keydown", onKeyDown, true);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown, true);
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, [menuOpen]);

    const persist = (value) => localStorage.setItem(STORAGE_KEY, String(value));

    const choosePreset = (preset) => {
        setSlippage(preset.value);
        setIsCustom(false);
        setCustomValue("");
        setMenuOpen(false);
        persist(preset.value);
        triggerRef.current?.focus();
    };

    const chooseCustom = () => {
        setIsCustom(true);
        setMenuOpen(false);
        // Let the field mount before focusing it
        requestAnimationFrame(() => customInputRef.current?.focus());
    };

    const handleCustomChange = (value) => {
        if (value === "") {
            setCustomValue("");
            return;
        }
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
            setCustomValue(value);
            setSlippage(numValue);
            persist(numValue);
        }
    };

    // Roving focus through the option list
    const onMenuKeyDown = useCallback((e) => {
        const items = Array.from(
            menuRef.current?.querySelectorAll('[role="option"]') || []
        );
        if (!items.length) return;
        const i = items.indexOf(document.activeElement);

        if (e.key === "ArrowDown") {
            e.preventDefault();
            items[(i + 1 + items.length) % items.length].focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            items[(i - 1 + items.length) % items.length].focus();
        } else if (e.key === "Home") {
            e.preventDefault();
            items[0].focus();
        } else if (e.key === "End") {
            e.preventDefault();
            items[items.length - 1].focus();
        }
    }, []);

    const activePreset = PRESETS.find((p) => p.value === slippage);
    const status = assess(slippage);
    const tone = status ? TONE_STYLES[status.tone] : null;

    return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth
            PaperProps={{
                className: "!rounded-[24px]",
                style: { background: 'none', boxShadow: 'none' }
            }}
        >
            <div className="card-3d modal-shell">

                {/* Pinned chrome */}
                <div className="modal-head">
                    <div className="flex items-center justify-between gap-3">
                        <h2 className="text-[18px] sm:text-[20px] font-bold gradient-text">
                            Settings
                        </h2>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close settings"
                            className="swap-arrow-3d w-9 h-9 flex items-center justify-center shrink-0"
                        >
                            <IoClose className="size-[18px]" />
                        </button>
                    </div>
                </div>

                <div className="modal-body">
                    <label
                        htmlFor="slippage-trigger"
                        className="block text-usdx-ink text-[14px] font-bold mb-1.5"
                    >
                        Slippage Tolerance
                    </label>
                    <p className="text-usdx-ink-subtle text-[12px] mb-4 leading-relaxed">
                        The most the price may move against you between quote and
                        confirmation. Anything worse and the trade reverts instead of
                        filling — you keep your tokens and pay only gas.
                    </p>

                    {/* Tolerance selector */}
                    <div className="relative">
                        <button
                            id="slippage-trigger"
                            ref={triggerRef}
                            type="button"
                            onClick={() => setMenuOpen((v) => !v)}
                            aria-haspopup="listbox"
                            aria-expanded={menuOpen}
                            className="inner-3d w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left"
                        >
                            <span className="flex items-center gap-2.5 min-w-0">
                                {tone && (
                                    <span
                                        className={`w-2 h-2 rounded-full shrink-0 ${tone.dot}`}
                                        aria-hidden="true"
                                    />
                                )}
                                <span className="num text-[16px] font-bold text-usdx-ink">
                                    {slippage}%
                                </span>
                                <span className="text-[11px] font-medium text-usdx-ink-subtle truncate">
                                    {isCustom ? "Custom" : activePreset?.note}
                                </span>
                            </span>
                            <IoChevronDown
                                className={`size-[16px] text-usdx-ink-subtle shrink-0 transition-transform duration-200 ${
                                    menuOpen ? "rotate-180" : ""
                                }`}
                                aria-hidden="true"
                            />
                        </button>

                        {/* Options. Expands in flow rather than overlaying — the modal
                            body is a scroll container, so an absolutely positioned
                            panel would be clipped by it at short viewport heights. */}
                        {menuOpen && (
                            <div
                                ref={menuRef}
                                role="listbox"
                                aria-labelledby="slippage-trigger"
                                onKeyDown={onMenuKeyDown}
                                className="mt-2 rounded-2xl border border-usdx-line bg-usdx-surface overflow-hidden shadow-usdx-card"
                            >
                                {PRESETS.map((preset) => {
                                    const selected = !isCustom && preset.value === slippage;
                                    return (
                                        <button
                                            key={preset.value}
                                            type="button"
                                            role="option"
                                            aria-selected={selected}
                                            onClick={() => choosePreset(preset)}
                                            className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left border-b border-usdx-line-soft last:border-b-0 transition-colors ${
                                                selected ? "option-selected" : "option-row"
                                            }`}
                                        >
                                            <span className="flex items-baseline gap-2.5 min-w-0">
                                                <span
                                                    className={`num text-[15px] font-bold ${
                                                        selected
                                                            ? "text-usdx-accent"
                                                            : "text-usdx-ink"
                                                    }`}
                                                >
                                                    {preset.label}
                                                </span>
                                                <span className="text-[11px] text-usdx-ink-subtle truncate">
                                                    {preset.note}
                                                </span>
                                            </span>
                                            {selected && (
                                                <IoCheckmark className="size-[17px] text-usdx-accent shrink-0" />
                                            )}
                                        </button>
                                    );
                                })}

                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={isCustom}
                                    onClick={chooseCustom}
                                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${
                                        isCustom ? "option-selected" : "option-row"
                                    }`}
                                >
                                    <span
                                        className={`text-[14px] font-semibold ${
                                            isCustom ? "text-usdx-accent" : "text-usdx-ink"
                                        }`}
                                    >
                                        Custom…
                                    </span>
                                    {isCustom && (
                                        <IoCheckmark className="size-[17px] text-usdx-accent shrink-0" />
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Custom entry, only once that option is chosen */}
                    {isCustom && (
                        <div className="relative mt-3">
                            <input
                                id="custom-slippage"
                                ref={customInputRef}
                                type="number"
                                min="0"
                                max="50"
                                step="0.1"
                                inputMode="decimal"
                                value={customValue}
                                onChange={(e) => handleCustomChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "-" || e.key === "e" || e.key === "E") {
                                        e.preventDefault();
                                    }
                                }}
                                placeholder="0.5"
                                aria-label="Custom slippage percentage"
                                className="num inner-3d w-full py-3.5 pl-4 pr-10 text-usdx-ink placeholder:text-usdx-ink-subtle/50 focus:border-usdx-accent/30 outline-none text-[16px] font-semibold"
                            />
                            <span
                                className="num absolute right-4 top-1/2 -translate-y-1/2 text-usdx-ink-subtle text-[15px] font-semibold pointer-events-none"
                                aria-hidden="true"
                            >
                                %
                            </span>
                        </div>
                    )}

                    {/* Guidance for the value in effect */}
                    {status && (
                        <div className={`mt-3 p-3 rounded-xl border flex items-start gap-2.5 ${tone.wrap}`}>
                            <span
                                className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${tone.dot}`}
                                aria-hidden="true"
                            />
                            <p className={`text-[12px] font-medium leading-relaxed ${tone.text}`}>
                                {status.text}
                            </p>
                        </div>
                    )}
                </div>

                {/* Pinned footer */}
                <div className="modal-foot flex items-center justify-between gap-3">
                    <span className="text-usdx-ink-subtle text-[12px] font-medium">
                        Applied to all trades
                    </span>
                    <span className="num text-usdx-accent font-bold text-[15px]">
                        {slippage}%
                    </span>
                </div>
            </div>
        </Dialog>
    );
};

export default SlippageSettings;
